import { useTranslation } from 'react-i18next';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import type { PurchaseOrderStatusCode } from '@/types/core/purchase-order/purchase-order';

const STATUS_VARIANT_BY_CODE: Record<PurchaseOrderStatusCode, BadgeVariant> = {
  ORDER_CREATION: 'gray',
  WAITING_FOR_SUPPLIER_AGREEMENT: 'blue',
  SUPPLIER_AGREEMENT_RECEIVED: 'green',
  IN_PRODUCTION: 'blue',
  IN_ADJUSTMENTS: 'orange',
  END_OF_PRODUCTION: 'green',
  CLOSED: 'gray',
  CANCELLED: 'destructive',
};

type PurchaseOrderStatusBadgeProps = {
  statusCode: PurchaseOrderStatusCode;
  className?: string;
  withBorder?: boolean;
};

export function PurchaseOrderStatusBadge({
  statusCode,
  className,
  withBorder = false,
}: PurchaseOrderStatusBadgeProps) {
  const { t } = useTranslation('purchaseOrder');
  const variant = STATUS_VARIANT_BY_CODE[statusCode] ?? 'gray';
  const label = t(`status.${statusCode}`);

  const borderColorByStatus: Partial<Record<PurchaseOrderStatusCode, string>> =
    {
      ORDER_CREATION: 'border-gray-400 dark:border-gray-500',
      WAITING_FOR_SUPPLIER_AGREEMENT: 'border-blue-400 dark:border-blue-500',
      SUPPLIER_AGREEMENT_RECEIVED: 'border-green-500 dark:border-green-600',
      IN_PRODUCTION: 'border-blue-400 dark:border-blue-500',
      IN_ADJUSTMENTS: 'border-orange-400 dark:border-orange-500',
      END_OF_PRODUCTION: 'border-green-500 dark:border-green-600',
      CLOSED: 'border-gray-500 dark:border-gray-600',
      CANCELLED: 'border-red-600 dark:border-red-700',
    };

  const borderClasses = withBorder
    ? `border-2 ${borderColorByStatus[statusCode] ?? ''}`
    : '';

  return (
    <Badge
      className={`rounded-full border px-4 py-1 font-medium text-xs uppercase tracking-wide ${borderClasses} ${
        className ?? ''
      }`}
      variant={variant}
    >
      {label}
    </Badge>
  );
}
